/*
 * Project: bellcloud
 * File: components/common/InstallAppBanner.tsx
 * Description: 모바일 브라우저 접속 시 "홈 화면에 추가(PWA 설치)"를 유도하는 전역 배너
 */

import { useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { InstallGuideModal } from './InstallGuideModal';

const BannerContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  padding: ${({ theme }) => theme.spacing[6]}px ${({ theme }) => theme.spacing[7]}px;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[5]}px;
  background: ${({ theme }) => theme.colors.surface.group.brand};
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[1]}px;
  min-width: 0;
`;

const MainText = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md}px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: 130%;
`;

const SubText = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  opacity: 0.7;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: 130%;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]}px;
  flex-shrink: 0;
`;

const InstallButton = styled.button`
  display: flex;
  padding: ${({ theme }) => theme.spacing[4]}px ${({ theme }) => theme.spacing[6]}px;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.button.bg.primary.default};
  color: ${({ theme }) => theme.colors.button.text.primary};
  font-size: ${({ theme }) => theme.fontSize.md}px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  line-height: 130%;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.button.bg.primary.hover};
  }

  &:active {
    transform: translateY(1px);
  }
`;

const DetailButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;
  align-self: flex-start;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: ${({ theme }) => theme.fontSize.xl}px;
  color: ${({ theme }) => theme.colors.text.primary};
  opacity: 0.7;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  transition: opacity 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 1;
  }
`;

/**
 * `useInstallPrompt`로 판단한 플랫폼(android/ios)에 따라 설치 버튼 또는 수동 안내를 보여준다.
 * 닫기는 로컬 state로만 처리하며 영구 저장하지 않는다 (새로고침/재방문 시 다시 노출).
 */
export const InstallAppBanner: React.FC = () => {
  const { t } = useTranslation();
  const { platform, promptInstall } = useInstallPrompt();
  const [dismissed, setDismissed] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  if (!platform || dismissed) return null;

  return (
    <>
      <BannerContainer>
        <TextContainer>
          {platform === 'android' ? (
            <>
              <MainText>{t(i18nKeys.common.install_app_android_title)}</MainText>
              <SubText>{t(i18nKeys.common.install_app_android_description)}</SubText>
            </>
          ) : (
            <>
              <MainText>{t(i18nKeys.common.install_app_ios_title)}</MainText>
              <SubText>{t(i18nKeys.common.install_app_ios_description)}</SubText>
              <SubText>
                {t(i18nKeys.common.install_app_ios_step_share)}{' '}
                {t(i18nKeys.common.install_app_ios_step_add)}
              </SubText>
              <DetailButton onClick={() => setIsGuideOpen(true)}>
                {t(i18nKeys.common.install_app_ios_detail_button)}
              </DetailButton>
            </>
          )}
        </TextContainer>
        <ActionGroup>
          {platform === 'android' && (
            <InstallButton onClick={promptInstall}>
              {t(i18nKeys.common.install_app_android_button)}
            </InstallButton>
          )}
          <CloseButton
            onClick={() => setDismissed(true)}
            aria-label={t(i18nKeys.common.install_app_close_label)}
          >
            ✕
          </CloseButton>
        </ActionGroup>
      </BannerContainer>
      {platform === 'ios' && (
        <InstallGuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
      )}
    </>
  );
};
